import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { format } from "date-fns";
import {
    Box, 
    Typography, 
    Button, 
    Grid, 
    TextField, 
    TableContainer, 
    Paper, 
    Table, 
    TableHead, 
    TableBody, 
    TableRow, 
    TableCell, 
    Link,
    ThemeProvider,
    Breadcrumbs,
    Card,
    CardContent,
    CardHeader,
    Snackbar,
    Alert,
    CircularProgress
} from '@mui/material';
import { createTheme } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { styled } from "@mui/material/styles";
import EventIcon from '@mui/icons-material/Event';
import { getBillingRecord } from '../API/billingAPI';
import { getClient, getCacheClients, putClient, putCacheClient } from '../API/clientAPI';
import { getJobOrder } from '../API/jobOrderAPI';


const theme = createTheme({
    palette: {
      primary: {
        main: "#1e5d45", // Custom primary color
      },
    },
  });
  
const StyledHr = styled("hr")(({ theme }) => ({
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(4, 0),
    opacity: 0.8,
}));


function ViewBillingRecord() {
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const [billingRecord, setBillingRecord] = useState({})
    const [loading, setLoading] = useState(true); // State to manage loading status
    const [error, setError] = useState(null); // State to manage error messages
    const [client, setClient] = useState({})
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");


    useEffect(() => {
        const fetchBillingRecord = async () => {
            try {
                const data = await getBillingRecord(id);  // Fetch billing record by ID

                if (!data) {
                    setError('Billing record not found');
                    setLoading(false);
                    return;
                }

                // Fetch client details based on client_id
                const fetchedClient = await getClient(data.client_id);
                setClient(fetchedClient)
                // Extract billing addresses and contacts
                const billingAddresses = fetchedClient.addresses.filter(address => address.billing);
                const billingContacts = fetchedClient.contacts.filter(contact => contact.billing);

                // Construct client data object
                const clientData = {
                    addresses: billingAddresses,
                    timestamp_update: fetchedClient.timestamp_update,
                    timestamp_create: fetchedClient.timestamp_create,
                    user_update: fetchedClient.user_update,
                    name: fetchedClient.name,
                    id: fetchedClient.id,
                    contacts: billingContacts,
                    user_create: fetchedClient.user_create
                };

                // Fetch and transform job orders
                const jobOrders = await Promise.all(
                    (data.job_orders || []).map(async (id) => {
                        const jobOrder = await getJobOrder(id);

                        // Transform job order
                        const formattedDate = format(new Date(jobOrder.job_date), "MMM dd, yyyy");

                        const jobScope = jobOrder.competencies
                            ?.filter((competency) => competency !== "driving_2_wheels" && competency !== "driving_4_wheels")
                            .map((competency) => {
                                const formattedCompetency = competency
                                    .replace(/_/g, ' ')
                                    .replace(/\b\w/g, (char) => char.toUpperCase());

                                const specializationKey = competency === "general_pest_control" ? "general_pest_control" : "termite";

                                const formattedSpecialization = (jobOrder.specialization?.[specializationKey] || []).map((word) =>
                                    word.length === 2
                                        ? word.toUpperCase()
                                        : word.charAt(0).toUpperCase() + word.slice(1)
                                );

                                return {
                                    competency: formattedCompetency,
                                    specialization: formattedSpecialization,
                                };
                            }) || [];

                        return {
                            id: jobOrder.id,
                            job_date: formattedDate,
                            jobScope: jobScope,
                            amount: jobOrder.service_fee || 0
                        };
                    })
                );

                // Calculate overdue days and update payment_status
                const calculatePaymentStatus = (due_date, currentStatus) => {
                    const paymentStatusMap = {
                        "30 days overdue": { min: 30, max: 59 },
                        "60 days overdue": { min: 60, max: 89 },
                        "90 days overdue": { min: 90, max: 119 },
                        "120 days overdue": { min: 120, max: 149 },
                        "150 days overdue": { min: 150, max: 179 },
                        "180 days overdue": { min: 180, max: 9999 },
                        ">180 days overdue": { min: 180, max: Infinity }
                    };

                    const today = new Date();
                    const dueDate = new Date(due_date);
                    const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));

                    if (["Not Billed", "Paid", "Not Due"].includes(currentStatus)) {
                        return currentStatus;
                    }

                    for (const [status, { min, max }] of Object.entries(paymentStatusMap)) {
                        if (daysOverdue >= min && daysOverdue <= max) {
                            return status;
                        }
                    }
                    return currentStatus;
                };

                const updatedPaymentStatus = calculatePaymentStatus(data.due_date, data.payment_status);

                // Update billing record state
                setBillingRecord({
                    ...data,
                    client: clientData,
                    jobOrders,
                    payment_status: updatedPaymentStatus
                });

            } catch (err) {
                setError('Failed to fetch billing record or related data');
            } finally {
                setLoading(false);  // Ensure loading is stopped
            }
        };

        if (id) fetchBillingRecord();  // Fetch only if ID exists
    }, [id]);  // Re-run when ID changes


    return (
        <ThemeProvider theme={theme}>
            {/* Success Snackbar */}
            <Snackbar
            open={!!successMessage}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            onClose={() => setSuccessMessage("")}
            >
            <Alert
                severity="success"
                sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "auto",
                maxWidth: "100%",
                mx: "auto",
                }}
            >
                {successMessage}
            </Alert>
            </Snackbar>
    
            {/* Loading Snackbar */}
            <Snackbar
            open={isLoading}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
            <Alert
                severity="info"
                icon={<CircularProgress size={18} />}
                sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "auto",
                maxWidth: "100%",
                mx: "auto",
                }}
            >
                Loading, please wait...
            </Alert>
            </Snackbar>
    
            {/* Error Snackbar */}
            <Snackbar
            open={!!errorMessage}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            onClose={() => setErrorMessage("")}
            >
            <Alert
                severity="error"
                sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "auto",
                maxWidth: "100%",
                mx: "auto",
                }}
            >
                {errorMessage}
            </Alert>
            </Snackbar>     
            <Box sx={{ padding: "40px 60px" }}>
                <Card
                    variant="outlined"
                    style={{
                        marginTop: "40px",
                        marginBottom: "40px",
                        borderRadius: 0,
                    }}
                >
                    <CardHeader
                        title={
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box display="flex" alignItems="center">
                            <EventIcon
                                sx={{
                                fontSize: "40px",
                                marginRight: "8px",
                                }}
                            />
                            <Typography variant="h6">Finance</Typography>
                            </Box>
                            <Breadcrumbs
                            aria-label="breadcrumb"
                            separator={<NavigateNextIcon fontSize="small" />}
                            sx={{ color: "white" }}
                            >
                            <Typography sx={{ color: "#ffffff99" }}>
                                Finance
                            </Typography>
                            <Typography
                                component={RouterLink}
                                to="/manage-billing-records"
                                sx={{ color: "#ffffff99", textDecoration: "none" }}
                            >
                                Manage Billing Records
                            </Typography>
                            <Typography sx={{ color: "#ffffff", textDecoration: "none" }}>
                                View Billing Record
                            </Typography>
                            </Breadcrumbs>
                        </Box>
                        }
                        sx={{
                        backgroundColor: "#1e5d45",
                        color: "white",
                        padding: "16px",
                        }}
                    />
                    <CardContent sx={{ padding: "20px" }}>
                        <Card variant="outlined" sx={{ padding: "20px" }}>
                            <CardHeader
                                title={
                                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                    View Billing Record
                                    </Typography>
                                }
                                sx={{ marginBottom: 2 }}
                            />

                            <Grid container spacing={3}>

                                {/* Client Information Section */}
                                <Grid
                                    item
                                    xs={12}
                                    md={12}
                                    sx={{ display: "flex", flexDirection: "column" }}
                                >
                                    <Card
                                        variant="outlined"
                                        sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                                    >
                                        <CardHeader
                                            title={
                                                <Typography
                                                variant="h6"
                                                sx={{
                                                    paddingBottom: "1rem",
                                                    fontWeight: "bold",
                                                    textTransform: "uppercase",
                                                    fontSize: "1rem",
                                                }}
                                                >
                                                Client Information
                                                </Typography>
                                            }
                                        />
                                        <CardContent
                                            sx={{
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            }}
                                        >
                                            <Grid container spacing={3}>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={3} >
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                label="Client Name"
                                                                variant="outlined"
                                                                fullWidth
                                                                value={billingRecord?.client?.name || ''}  // Display client name or empty string if not available
                                                                disabled
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Category"
                                                        variant="outlined"
                                                        value={client?.category || ''}
                                                        disabled
                                                    />
                                                </Grid>

                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Parent Client"
                                                        variant="outlined"
                                                        value={client?.parent_client?.name || ''}
                                                        disabled
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={8}>
                                                            <TextField
                                                                fullWidth
                                                                label="Billing Address"
                                                                value={
                                                                    billingRecord?.client?.addresses?.[0]?.house_unit_street
                                                                        ? `${billingRecord.client.addresses[0].house_unit_street}, ${billingRecord.client.addresses[0].barangay}, ${billingRecord.client.addresses[0].city}`
                                                                        : ''
                                                                }
                                                                disabled
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Grid container spacing={3} >
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                fullWidth
                                                                label="Billing Contact"
                                                                value={
                                                                    billingRecord?.client?.contacts?.[0]
                                                                        ? `${billingRecord.client.contacts[0].firstname} ${billingRecord.client.contacts[0].lastname}`
                                                                        : ''
                                                                }
                                                                disabled
                                                            />
                                                        </Grid>

                                                        <Grid item xs={4}>
                                                            {
                                                                billingRecord?.client?.contacts?.[0]?.numbers?.length > 0
                                                                    ? billingRecord.client.contacts[0].numbers.map((number, index) => (
                                                                        <TextField
                                                                            key={index}
                                                                            fullWidth
                                                                            label="Billing Number"
                                                                            value={`${number.type}: ${number.number}`}
                                                                            disabled
                                                                            sx={{ marginBottom: 2 }}
                                                                        />
                                                                    ))
                                                                    : (
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Billing Number"
                                                                            value=""
                                                                            disabled
                                                                        />
                                                                    )
                                                            }
                                                        </Grid>

                                                        <Grid item xs={4}>
                                                            {
                                                                billingRecord?.client?.contacts?.[0]?.emails?.length > 0
                                                                    ? billingRecord.client.contacts[0].emails.map((email, index) => (
                                                                        <TextField
                                                                            key={index}
                                                                            fullWidth
                                                                            label="Billing Email"
                                                                            value={email.email}
                                                                            disabled
                                                                            sx={{ marginBottom: 2 }}
                                                                        />
                                                                    ))
                                                                    : (
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Billing Email"
                                                                            value=""
                                                                            disabled
                                                                        />
                                                                    )
                                                            }
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Billing Information Section */}
                                <Grid
                                    item
                                    xs={12}
                                    md={12}
                                    sx={{ display: "flex", flexDirection: "column" }}
                                >
                                    <Card
                                        variant="outlined"
                                        sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                                    >
                                        <CardHeader
                                            title={
                                                <Typography
                                                variant="h6"
                                                sx={{
                                                    paddingBottom: "1rem",
                                                    fontWeight: "bold",
                                                    textTransform: "uppercase",
                                                    fontSize: "1rem",
                                                }}
                                                >
                                                Billing Information
                                                </Typography>
                                            }
                                        />
                                        <CardContent
                                            sx={{
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            }}
                                        >
                                            <Grid container spacing={3}>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                fullWidth
                                                                label="Billing Invoice Number"
                                                                value={billingRecord.billing_invoice_number || ''}
                                                                disabled
                                                            />
                                                        </Grid>
                                                        <Grid item xs={2}>
                                                            <TextField
                                                                fullWidth
                                                                label="Billing Date"
                                                                type="date"
                                                                value={billingRecord.billing_date || ''}
                                                                InputLabelProps={{
                                                                    shrink: true,                                                               
                                                                }}
                                                                disabled
                                                            />
                                                        </Grid>
                                                        <Grid item xs={2}>
                                                            <TextField
                                                                fullWidth
                                                                label="Due Date"
                                                                type="date"
                                                                value={billingRecord.due_date || ''}
                                                                InputLabelProps={{
                                                                    shrink: true,                                                               
                                                                }}
                                                                disabled
                                                            />
                                                        </Grid>                                                        
                                                    </Grid>
                                                </Grid>                                                
                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Prepared By"
                                                        value={billingRecord.prepared_by || ''}
                                                        disabled
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Verified By"
                                                        value={billingRecord.verified_by || ''}
                                                        disabled
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                fullWidth
                                                                label="Payment Status"
                                                                value={billingRecord.payment_status || ''}
                                                                disabled
                                                            />
                                                        </Grid>
                                                        <Grid item xs={2}>
                                                            <TextField
                                                                fullWidth
                                                                label="Payment Date"
                                                                type="date"
                                                                value={billingRecord.payment_date || ''}
                                                                InputLabelProps={{
                                                                    shrink: true,                                                               
                                                                }}
                                                                disabled
                                                            />
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                fullWidth
                                                                label="Collected By"
                                                                value={billingRecord.collected_by || ''}                                                                
                                                                disabled
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Cost Breakdown Section */}
                                <Grid
                                    item
                                    xs={12}
                                    md={12}
                                    sx={{ display: "flex", flexDirection: "column" }}
                                >
                                    <Card
                                        variant="outlined"
                                        sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                                    >
                                        <CardHeader
                                            title={
                                                <Typography
                                                variant="h6"
                                                sx={{
                                                    paddingBottom: "1rem",
                                                    fontWeight: "bold",
                                                    textTransform: "uppercase",
                                                    fontSize: "1rem",
                                                }}
                                                >
                                                Cost Breakdown
                                                </Typography>
                                            }
                                        />
                                        <CardContent
                                            sx={{
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            }}
                                        >
                                            <Grid container spacing={3}>
                                                <Grid item xs={12}>
                                                    <TableContainer component={Paper}>
                                                        <Table style={{ tableLayout: 'fixed', width: '100%' }}>
                                                            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                                                                <TableRow>
                                                                    <TableCell style={{ width: '25%' }} sx={{ fontWeight: "bold" }}>Date</TableCell>
                                                                    <TableCell style={{ width: '40%' }} sx={{ fontWeight: "bold" }}>Job Scope</TableCell>
                                                                    <TableCell style={{ width: '25%' }} sx={{ fontWeight: "bold" }}>Amount</TableCell>
                                                                    <TableCell style={{ width: '10%' }}></TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody sx={{ backgroundColor: "#fbfbfb" }}>
                                                                {billingRecord?.jobOrders?.map((jobOrder, index) => (
                                                                    <TableRow key={index}>
                                                                        <TableCell>{jobOrder?.job_date}</TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2">
                                                                                <b>Job Order ID:</b>{' '}
                                                                                <Link
                                                                                    href={`/view-job-order/${jobOrder?.id}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    style={{ textDecoration: 'none', color: '#1976d2' }}
                                                                                >
                                                                                    {jobOrder?.id}
                                                                                </Link>
                                                                            </Typography>

                                                                            {jobOrder?.jobScope?.length > 0 ? (
                                                                                <ul style={{ margin: 0, padding: '0 0 0 16px', listStyleType: 'disc' }}>
                                                                                    {jobOrder?.jobScope?.map((scope, scopeIndex) => (
                                                                                        <li key={scopeIndex}>
                                                                                            <Typography variant="body2">
                                                                                                <b>{scope?.competency}</b>
                                                                                                {scope?.specialization?.length > 0
                                                                                                    ? `: ${scope?.specialization?.join(', ')}`
                                                                                                    : ''}
                                                                                            </Typography>
                                                                                        </li>
                                                                                    ))}
                                                                                </ul>
                                                                            ) : (
                                                                                <Typography variant="body2">No specific scope defined.</Typography>
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <TextField
                                                                                fullWidth
                                                                                value={jobOrder?.amount || 0}
                                                                                InputProps={{                                                                                    
                                                                                    style: { fontSize: '0.875rem' },
                                                                                }}
                                                                                InputLabelProps={{
                                                                                    shrink: true,
                                                                                    style: { fontSize: '0.875rem' },
                                                                                }}
                                                                                disabled
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell style={{ textAlign: 'center' }}>
                                                                            <Typography variant="body2">-</Typography>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                                <TableRow>
                                                                    <TableCell/>
                                                                    <TableCell/>
                                                                    <TableCell/>
                                                                    <TableCell/>                                                                
                                                                </TableRow>                                                                    
                                                            </TableBody>
                                                        </Table>

                                                        <Table style={{ tableLayout: 'fixed', width: '100%' }}>
                                                            <TableBody sx={{ backgroundColor: "#fbfbfb" }}>
                                                                <TableRow>
                                                                    <TableCell rowSpan={3} colSpan={4}/>                                                              
                                                                    <TableCell sx={{ fontWeight: "bold" }}>Net Amount</TableCell>
                                                                    <TableCell align="right">{billingRecord.net_amount || 0}</TableCell>
                                                                    <TableCell/>
                                                                </TableRow>
                                                                <TableRow>
                                                                    <TableCell sx={{ fontWeight: "bold" }}>VAT @ 12%</TableCell>
                                                                    <TableCell align="right">{billingRecord.tax_amount || 0}</TableCell>
                                                                    <TableCell/>                     
                                                                </TableRow>
                                                                <TableRow> 
                                                                    <TableCell sx={{ fontWeight: "bold" }}>Total Amount</TableCell>
                                                                    <TableCell align="right">{billingRecord.total_amount || 0}</TableCell>
                                                                    <TableCell/>                    
                                                                </TableRow>
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>                                

                                {/* Form buttons */}
                                <Grid
                                    item
                                    xs={12}
                                    display="flex"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    mt={2}
                                    mb={2}
                                    mr={2}
                                    >                                        
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => navigate('/manage-billing-records')}
                                        sx={{
                                        marginLeft: "10px",
                                        fontSize: "0.875rem",
                                        }}
                                        disabled={isLoading}
                                    >
                                        Back
                                    </Button>                             
                                </Grid>

                            </Grid>
                        </Card>
                    </CardContent> 
                </Card>
            </Box>
        </ThemeProvider>
    );
}

export default ViewBillingRecord;
